/**
 * i18n の React 側。`viewpoint` と同じ購読パターンに揃えてある
 * （仕組みを2つ持たない）。
 */
import { useCallback, useEffect, useState } from "react";
import { i18n, translate, type Lang } from "./i18n.js";

/** 翻訳関数の型。フック外のヘルパに渡すときに使う。 */
export type Tr = (key: string, params?: Record<string, string | number>) => string;

export function useI18n(): {
  t: Tr;
  lang: Lang;
  setLang: (l: Lang) => void;
} {
  const [lang, setLangState] = useState<Lang>(i18n.lang);
  useEffect(() => i18n.subscribe(setLangState), []);

  const t = useCallback(
    (key: string, params?: Record<string, string | number>) => translate(lang, key, params),
    [lang],
  );
  return { t, lang, setLang: (l: Lang) => i18n.set(l) };
}

/**
 * 日時の表示。**ロケールで書式だけ変える。**
 * 期限の計算は常に Asia/Tokyo（`deadline.ts`）なので、
 * ここで変わるのは見た目だけ。
 */
export function formatDateTime(iso: string | Date, _lang: Lang): string {
  // L-03: 日付の向きを言語で変えない（06/10/2026 と 2026-10-02 が混ざると読み違える）。全画面で 2026-10-06 14:05
  const d = typeof iso === "string" ? new Date(iso) : iso;
  if (Number.isNaN(d.getTime())) return typeof iso === "string" ? iso : "";
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Tokyo", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23",
  }).formatToParts(d).map((p) => [p.type, p.value]));
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;
}
